/**
 * Kalkulator nawodnienia.
 *
 * Skrypt niczego nie dorenderowuje - tabelka przykładowa, tabela "co się
 * wlicza" i wszystkie teksty stoją w źródle HTML. Tutaj dzieje się tylko
 * przeliczanie na bieżąco, przełączanie stanu A/B panelu wyniku, komunikaty
 * warunkowe i przyklejany pasek nawigacji.
 *
 * Wszystkie współczynniki i progi skrypt czyta z atrybutów data-* sekcji,
 * które szablon wypełnia z pliku inc/data/hydration.php - nie ma drugiej
 * kopii wartości w kodzie. Wzory muszą dawać ten sam wynik co metody
 * klasy bloku Hydration_Calculator (PHP liczy tabelkę stanu A w źródle).
 *
 * Część licząca (obiekt `calc`) to czyste funkcje bez DOM. Gdy plik ładuje
 * Node (testy/test-calc.js), eksportuje tylko ten obiekt i nie dotyka DOM.
 *
 * Vanilla JS, bez jQuery.
 */
(function () {
	'use strict';

	var NBSP = ' '; // spacja nierozdzielająca między liczbą a jednostką, jak number_format() w PHP

	function toNumber(value) {
		if (value === null || value === '' || typeof value === 'undefined') {
			return NaN;
		}
		var parsed = parseFloat(String(value).replace(',', '.'));
		return isNaN(parsed) ? NaN : parsed;
	}

	/**
	 * Ucina śmieci zmiennoprzecinkowe przed floor()/round(), tak jak
	 * floor_kcal() we wzorcu: 4125 × 0,8 to w IEEE 754 potencjalnie
	 * 3299,9999999999995, a matematycznie równo 3300.
	 */
	function round6(value) {
		return Math.round(value * 1e6) / 1e6;
	}

	// --- Część licząca: czyste funkcje, bez DOM ----------------------------

	var calc = {

		/**
		 * Odczyt pola liczbowego: { value, empty, invalid }.
		 * Puste pole to osobny stan (nie błąd), bo masa ciała startuje pusta.
		 */
		readField: function (raw, min, max) {
			var text = raw === null || typeof raw === 'undefined' ? '' : String(raw).trim();

			if (text === '') {
				return { value: null, empty: true, invalid: false };
			}

			var value = toNumber(text);
			if (isNaN(value) || value < min || value > max) {
				return { value: null, empty: false, invalid: true };
			}

			return { value: value, empty: false, invalid: false };
		},

		/**
		 * baza_do_wypicia_ml = (masa_kg × 33) × 0,80 - sekcja 3.1 briefu.
		 */
		baseMl: function (weight, cfg) {
			return (weight * cfg.mlPerKg) * cfg.drinkShare;
		},

		/**
		 * Dodatek treningowy, proporcjonalny: wartość na 30 minut × minuty / 30.
		 * Przy czasie 0 intensywność nie ma znaczenia (pole jest wyszarzone).
		 */
		trainingMl: function (minutes, intensity, cfg) {
			var rate = cfg.training[intensity];
			if (!minutes || !rate) {
				return 0;
			}
			return (rate * minutes) / cfg.trainingBlockMinutes;
		},

		/**
		 * Ciąża i karmienie zaznaczone razem: obowiązuje wyższa wartość,
		 * dodatki się nie sumują (sekcja 3.3).
		 */
		extraMl: function (pregnancy, lactation, cfg) {
			if (pregnancy && lactation) {
				return Math.max(cfg.pregnancy, cfg.lactation);
			}
			if (pregnancy) {
				return cfg.pregnancy;
			}
			if (lactation) {
				return cfg.lactation;
			}
			return 0;
		},

		/**
		 * do_wypicia_ml - surowa, przed zaokrągleniem do wyświetlenia.
		 */
		totalMl: function (weight, settings, cfg) {
			return calc.baseMl(weight, cfg)
				+ calc.trainingMl(settings.minutes, settings.intensity, cfg)
				+ (settings.heat ? cfg.heat : 0)
				+ calc.extraMl(settings.pregnancy, settings.lactation, cfg);
		},

		/**
		 * Wyświetlana liczba: w dół do pełnych 100 ml (display_step_ml).
		 */
		displayMl: function (totalMl, cfg) {
			return Math.floor(round6(totalMl) / cfg.displayStepMl) * cfg.displayStepMl;
		},

		/**
		 * Litry z jednym miejscem po przecinku, przecinek dziesiętny: 1400 → "1,4".
		 */
		formatLiters: function (displayMl) {
			var tenths = Math.round(displayMl / 100);
			var whole = Math.floor(tenths / 10);
			var rest = tenths - whole * 10;
			return whole + ',' + rest;
		},

		/**
		 * szklanki = round(wyswietlane_ml / 250) - z wartości wyświetlanej
		 * (zaokrąglonej w dół do 100 ml), żeby liczby na ekranie się zgadzały:
		 * 1,8 l → 7 szklanek. Decyzja z 10.09.2026, odstępstwo od wzoru 3.1
		 * briefu (tam z wartości surowej).
		 */
		glasses: function (displayMl, cfg) {
			return Math.round(displayMl / cfg.glassMl);
		},

		/**
		 * Komunikaty warunkowe w kolejności z sekcji 3.4: >4 l, >3 l, długi
		 * trening (komunikat o ciąży/karmieniu usunięty decyzją z 10.09.2026,
		 * ZMIANY-COPY.md). Najwyżej maxMessages naraz. Progi litrowe wymagają
		 * wyniku (stan B); długi trening zależy tylko od pola, więc pokazuje się
		 * także w stanie A.
		 */
		messages: function (state, cfg) {
			var candidates = [
				{ key: 'over-4l', on: state.hasResult && state.totalMl > cfg.over4lMl },
				{ key: 'over-3l', on: state.hasResult && state.totalMl > cfg.over3lMl },
				{ key: 'long-training', on: state.minutes !== null && !isNaN(state.minutes) && state.minutes > cfg.longTrainingMinutes }
			];

			var shown = [];
			candidates.forEach(function (candidate) {
				if (candidate.on && shown.length < cfg.maxMessages) {
					shown.push(candidate.key);
				}
			});
			return shown;
		},

		/**
		 * Pełna ocena wejścia użytkownika (surowe teksty pól + pola wyboru).
		 */
		evaluate: function (input, cfg) {
			var weight = calc.readField(input.weight, cfg.weightMin, cfg.weightMax);
			var minutes = calc.readField(input.minutes, cfg.minutesMin, cfg.minutesMax);
			var hasResult = weight.value !== null && minutes.value !== null;

			var settings = {
				minutes: minutes.value === null ? 0 : minutes.value,
				intensity: input.intensity,
				heat: !!input.heat,
				pregnancy: !!input.pregnancy,
				lactation: !!input.lactation
			};

			var result = {
				weight: weight,
				minutes: minutes,
				intensityDisabled: minutes.value === 0,
				hasResult: hasResult,
				totalMl: null,
				displayMl: null,
				liters: null,
				glasses: null,
				messages: []
			};

			if (hasResult) {
				result.totalMl = calc.totalMl(weight.value, settings, cfg);
				result.displayMl = calc.displayMl(result.totalMl, cfg);
				result.liters = calc.formatLiters(result.displayMl);
				result.glasses = calc.glasses(result.displayMl, cfg);
			}

			result.messages = calc.messages({
				hasResult: hasResult,
				totalMl: result.totalMl,
				minutes: minutes.value
			}, cfg);

			return result;
		},

		/**
		 * Tabelka przykładowa (stan A): te same ustawienia, cztery masy ciała.
		 */
		samples: function (weights, settings, cfg) {
			return weights.map(function (weight) {
				var total = calc.totalMl(weight, settings, cfg);
				var display = calc.displayMl(total, cfg);
				return {
					weight: weight,
					totalMl: total,
					displayMl: display,
					liters: calc.formatLiters(display)
				};
			});
		}
	};

	// W Node (testy) eksportujemy tylko część liczącą i kończymy.
	if (typeof module !== 'undefined' && module.exports && typeof document === 'undefined') {
		module.exports = calc;
		return;
	}

	// --- Część przeglądarkowa ------------------------------------------------

	/**
	 * Pozycja wartości na torze suwaka - odpowiednik fill_percent() w PHP.
	 */
	function fillPercent(value, min, max) {
		if (!(max > min)) {
			return 0;
		}
		return Math.min(100, Math.max(0, ((value - min) / (max - min)) * 100));
	}

	function readConfig(root) {
		var get = function (name) {
			return toNumber(root.getAttribute('data-' + name));
		};

		return {
			mlPerKg: get('ml-per-kg'),
			drinkShare: get('drink-share'),
			displayStepMl: get('display-step-ml'),
			glassMl: get('glass-ml'),
			training: {
				light: get('training-light'),
				moderate: get('training-moderate'),
				intense: get('training-intense')
			},
			trainingBlockMinutes: get('training-block-minutes'),
			heat: get('heat'),
			pregnancy: get('pregnancy'),
			lactation: get('lactation'),
			over3lMl: get('over-3l-ml'),
			over4lMl: get('over-4l-ml'),
			longTrainingMinutes: get('long-training-minutes'),
			maxMessages: get('max-messages'),
			weightMin: get('weight-min'),
			weightMax: get('weight-max'),
			minutesMin: get('minutes-min'),
			minutesMax: get('minutes-max')
		};
	}

	function toggle(element, visible) {
		if (element) {
			element.hidden = !visible;
		}
	}

	function checkedValue(inputs, fallback) {
		for (var i = 0; i < inputs.length; i++) {
			if (inputs[i].checked) {
				return inputs[i].value;
			}
		}
		return fallback;
	}

	function initCalculator(root) {
		var cfg = readConfig(root);

		var weightNumber = root.querySelector('[data-role="weight-number"]');
		var weightRange = root.querySelector('[data-role="weight-range"]');
		var weightFill = root.querySelector('[data-role="weight-fill"]');
		var weightError = root.querySelector('[data-role="weight-error"]');
		var minutesNumber = root.querySelector('[data-role="minutes-number"]');
		var minutesRange = root.querySelector('[data-role="minutes-range"]');
		var minutesFill = root.querySelector('[data-role="minutes-fill"]');
		var minutesError = root.querySelector('[data-role="minutes-error"]');
		var intensityField = root.querySelector('[data-field="intensity"]');
		var emptyState = root.querySelector('[data-state="empty"]');
		var valueState = root.querySelector('[data-state="value"]');
		var litersEl = root.querySelector('[data-role="liters"]');
		var glassesEl = root.querySelector('[data-role="glasses"]');
		var liveEl = root.querySelector('[data-role="live"]');
		var messagesWrap = root.querySelector('[data-role="messages"]');
		var captionEl = root.querySelector('[data-role="samples-caption"]');

		if (!weightNumber || !minutesNumber || !emptyState || !valueState) {
			return;
		}

		var intensityInputs = Array.prototype.slice.call(root.querySelectorAll('[data-role="intensity"]'));
		var weatherInputs = Array.prototype.slice.call(root.querySelectorAll('[data-role="weather"]'));
		var pregnancyInput = root.querySelector('[data-role="pregnancy"]');
		var lactationInput = root.querySelector('[data-role="lactation"]');
		var sampleRows = Array.prototype.slice.call(root.querySelectorAll('[data-role="sample-row"]'));
		var sampleWeights = sampleRows.map(function (row) {
			return toNumber(row.getAttribute('data-weight'));
		});

		// Odświeżenie strony ma wracać do stanu wyjściowego (brief 3.5, 4.1).
		// Przeglądarki potrafią przywrócić wpisane wartości po przeładowaniu,
		// więc na starcie jawnie wracamy do wartości z atrybutów HTML.
		[weightNumber, minutesNumber].forEach(function (input) {
			input.value = input.defaultValue;
		});
		intensityInputs.concat(weatherInputs, [pregnancyInput, lactationInput]).forEach(function (input) {
			if (input) {
				input.checked = input.defaultChecked;
			}
		});

		// Ostatni poprawny czas - tabelka przykładowa nie może zgubić wartości,
		// gdy użytkownik skasuje pole albo wpisze liczbę spoza zakresu.
		var lastMinutes = toNumber(minutesNumber.value);
		if (isNaN(lastMinutes)) {
			lastMinutes = cfg.minutesMin;
		}

		function readSettings(minutes) {
			return {
				minutes: minutes,
				intensity: checkedValue(intensityInputs, 'moderate'),
				heat: checkedValue(weatherInputs, 'normal') === 'heat',
				pregnancy: !!(pregnancyInput && pregnancyInput.checked),
				lactation: !!(lactationInput && lactationInput.checked)
			};
		}

		function updateSamples(settings) {
			var rows = calc.samples(sampleWeights, settings, cfg);
			rows.forEach(function (sample, index) {
				var cell = sampleRows[index].querySelector('[data-role="sample-value"]');
				if (cell && !isNaN(sample.weight)) {
					cell.textContent = sample.liters + NBSP + 'l';
				}
			});

			// Podpis pod tabelką: zdanie z briefu przy domyślnym treningu i pogodzie,
			// po ich zmianie wariant "dla wybranych ustawień" (decyzja z 10.09.2026).
			if (captionEl) {
				var custom = settings.minutes > 0 || settings.heat;
				captionEl.textContent = captionEl.getAttribute(custom ? 'data-caption-custom' : 'data-caption-default') || '';
			}
		}

		/**
		 * Komunikaty warunkowe: maksymalnie dwa naraz, w kolejności z briefu.
		 * Klasa has-conditional steruje odstępem bloku od narzędzia.
		 */
		function updateMessages(keys) {
			var all = Array.prototype.slice.call(root.querySelectorAll('[data-message]'));
			all.forEach(function (element) {
				toggle(element, keys.indexOf(element.getAttribute('data-message')) !== -1);
			});
			if (messagesWrap) {
				messagesWrap.classList.toggle('has-conditional', keys.length > 0);
			}
		}

		function render() {
			var input = {
				weight: weightNumber.value,
				minutes: minutesNumber.value,
				intensity: checkedValue(intensityInputs, 'moderate'),
				heat: checkedValue(weatherInputs, 'normal') === 'heat',
				pregnancy: !!(pregnancyInput && pregnancyInput.checked),
				lactation: !!(lactationInput && lactationInput.checked)
			};

			var state = calc.evaluate(input, cfg);

			toggle(weightError, state.weight.invalid);
			toggle(minutesError, state.minutes.invalid || state.minutes.empty);

			if (state.minutes.value !== null) {
				lastMinutes = state.minutes.value;
			}

			// Suwaki i pola liczbowe to jedna wartość w dwóch kontrolkach.
			if (weightRange) {
				weightRange.classList.toggle('is-empty', state.weight.value === null);
				if (state.weight.value !== null) {
					weightRange.value = Math.min(cfg.weightMax, Math.max(cfg.weightMin, Math.round(state.weight.value)));
					weightRange.setAttribute('aria-valuetext', state.weight.value + ' kg');
				} else {
					weightRange.value = cfg.weightMin;
					weightRange.setAttribute('aria-valuetext', 'nie podano');
				}
			}
			if (weightFill) {
				// Pusta masa ciała = pusty tor, żeby nic nie sugerowało wyniku.
				weightFill.style.width = state.weight.value === null
					? '0%'
					: fillPercent(state.weight.value, cfg.weightMin, cfg.weightMax) + '%';
			}
			if (minutesRange && state.minutes.value !== null) {
				minutesRange.value = Math.min(cfg.minutesMax, Math.max(cfg.minutesMin, Math.round(state.minutes.value)));
			}
			if (minutesFill) {
				minutesFill.style.width = fillPercent(lastMinutes, cfg.minutesMin, cfg.minutesMax) + '%';
			}

			// Pole intensywności wyszarzone przy czasie treningu równym zero (3.5).
			if (intensityField) {
				intensityField.disabled = lastMinutes === 0;
			}

			var settings = readSettings(lastMinutes);
			updateSamples(settings);

			if (state.hasResult) {
				if (litersEl) {
					litersEl.textContent = state.liters;
				}
				if (glassesEl) {
					glassesEl.textContent = String(state.glasses);
				}
			}

			toggle(emptyState, !state.hasResult);
			toggle(valueState, state.hasResult);

			// Komunikaty liczymy z ostatniego poprawnego czasu, jak tabelkę.
			updateMessages(calc.messages({
				hasResult: state.hasResult,
				totalMl: state.totalMl,
				minutes: lastMinutes
			}, cfg));

			if (liveEl) {
				liveEl.textContent = state.hasResult
					? 'Wynik: ' + state.liters + ' litra płynów, około ' + state.glasses + ' szklanek po 250 ml.'
					: '';
			}
		}

		[weightNumber, minutesNumber].forEach(function (input) {
			input.addEventListener('input', render);
		});

		if (weightRange) {
			weightRange.addEventListener('input', function () {
				weightNumber.value = weightRange.value;
				render();
			});
		}

		if (minutesRange) {
			minutesRange.addEventListener('input', function () {
				minutesNumber.value = minutesRange.value;
				render();
			});
		}

		intensityInputs.concat(weatherInputs).forEach(function (input) {
			input.addEventListener('change', render);
		});

		[pregnancyInput, lactationInput].forEach(function (input) {
			if (input) {
				input.addEventListener('change', render);
			}
		});

		render();
	}

	/**
	 * Nawigacja kotwicowa dokleja się pod header motywu i jedzie z czytelnikiem
	 * do końca strony. Pozycję przełącza skrypt, a nie position: sticky, bo
	 * sticky przestaje działać na końcu sekcji bloku - pasek odjeżdżałby
	 * razem z tabelą, zamiast towarzyszyć dalszym sekcjom strony.
	 */
	function initStickyNav(root) {
		var nav = root.querySelector('[data-role="nav"]');
		if (!nav) {
			return;
		}

		var header = document.querySelector('.site-header');
		var spacer = document.createElement('div');
		spacer.className = 'hydration-calculator__nav-spacer';
		spacer.hidden = true;
		nav.parentNode.insertBefore(spacer, nav);

		var isFixed = false;
		var ticking = false;

		var list = nav.querySelector('ul');
		var links = Array.prototype.slice.call(nav.querySelectorAll('a[href^="#"]'));
		var targets = links.map(function (link) {
			var id = link.getAttribute('href').slice(1);
			return id ? document.getElementById(id) : null;
		});
		var activeIndex = -1;

		function headerOffset() {
			if (!header) {
				return 0;
			}
			var position = window.getComputedStyle(header).position;
			if (position !== 'sticky' && position !== 'fixed') {
				return 0;
			}
			var rect = header.getBoundingClientRect();
			return rect.top <= 0 ? Math.max(0, Math.round(rect.bottom)) : 0;
		}

		function keepVisible(link) {
			if (!list || list.scrollWidth <= list.clientWidth) {
				return;
			}
			var item = link.parentNode.getBoundingClientRect();
			var box = list.getBoundingClientRect();
			var margin = 16;

			if (item.left < box.left + margin) {
				list.scrollLeft += item.left - box.left - margin;
			} else if (item.right > box.right - margin) {
				list.scrollLeft += item.right - box.right + margin;
			}
		}

		function updateActive(offset) {
			if (!links.length) {
				return;
			}

			var line = offset + (isFixed ? nav.getBoundingClientRect().height : 0) + 16;
			var current = -1;

			targets.forEach(function (target, index) {
				if (target && target.getBoundingClientRect().top <= line) {
					current = index;
				}
			});

			// Sekcja, która cała przewinęła się nad linię odczytu, nie jest już
			// widoczna - aktywna staje się następna istniejąca. Bez tego między
			// panelem wyniku a tabelą pigułka pokazywałaby "Twój wynik", choć
			// pod paskiem widać już tabelę (odstępstwo od skryptu spalania).
			if (current !== -1 && targets[current].getBoundingClientRect().bottom < line) {
				for (var j = current + 1; j < targets.length; j++) {
					if (targets[j]) {
						current = j;
						break;
					}
				}
			}

			var atBottom = window.innerHeight + window.pageYOffset >= document.documentElement.scrollHeight - 2;
			if (atBottom) {
				for (var i = targets.length - 1; i >= 0; i--) {
					if (targets[i]) {
						current = i;
						break;
					}
				}
			}

			if (current === activeIndex) {
				return;
			}

			if (links[activeIndex]) {
				links[activeIndex].classList.remove('is-active');
				links[activeIndex].removeAttribute('aria-current');
			}

			activeIndex = current;

			if (links[activeIndex]) {
				links[activeIndex].classList.add('is-active');
				links[activeIndex].setAttribute('aria-current', 'true');
				keepVisible(links[activeIndex]);
			}
		}

		function update() {
			ticking = false;

			var offset = headerOffset();
			root.style.setProperty('--hc-sticky-top', offset + 'px');

			var top = (isFixed ? spacer : nav).getBoundingClientRect().top;

			if (!isFixed && top <= offset) {
				var margin = parseFloat(window.getComputedStyle(nav).marginBottom) || 0;
				spacer.style.height = nav.getBoundingClientRect().height + margin + 'px';
				spacer.hidden = false;
				nav.classList.add('is-fixed');
				isFixed = true;
			} else if (isFixed && top > offset) {
				nav.classList.remove('is-fixed');
				spacer.hidden = true;
				isFixed = false;
			}

			updateActive(offset);
		}

		function onScroll() {
			if (!ticking) {
				ticking = true;
				window.requestAnimationFrame(update);
			}
		}

		window.addEventListener('scroll', onScroll, { passive: true });
		window.addEventListener('resize', onScroll);
		window.addEventListener('load', onScroll);

		update();
	}

	function init() {
		var blocks = Array.prototype.slice.call(document.querySelectorAll('[data-hydration]'));

		blocks.forEach(function (root) {
			initCalculator(root);
			initStickyNav(root);
		});
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init);
	} else {
		init();
	}
})();
